import React, {useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
import {CompanyAdmin} from '../core/_models';
import {deleteCompanyGroupBrandingAsset} from '../core/_requests';
import {showErrorToast} from '../../../shared/utils/apiErrorMessage';

const API_URL = process.env.REACT_APP_API_URL || '';

const resolveAssetUrl = (value?: string | null): string | null => {
    const raw = String(value || '').trim();
    if (!raw || /^javascript:/i.test(raw)) return null;
    if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
    const normalized = raw.startsWith('/') ? raw : raw.startsWith('uploads/') ? `/${raw}` : raw;
    if (!normalized.startsWith('/')) return normalized;
    if (!API_URL) return normalized;
    try {
        return new URL(normalized, API_URL).toString();
    } catch {
        return normalized;
    }
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
    companyAdminData?: CompanyAdmin | null;
    onBrandingUpdated?: (updated: CompanyAdmin) => void;
}

const ViewCompanyAdminModal: React.FC<Props> = ({isOpen, onClose, companyAdminData, onBrandingUpdated}) => {
    const [currentData, setCurrentData] = useState<CompanyAdmin | null>(null);
    const [removing, setRemoving] = useState<'logo' | 'watermark' | null>(null);

    useEffect(() => {
        if (isOpen && companyAdminData) {
            setCurrentData(companyAdminData);
            setRemoving(null);
        }
    }, [isOpen, companyAdminData]);

    const logoUrl = useMemo(() => resolveAssetUrl(currentData?.logoUrl), [currentData?.logoUrl]);
    const watermarkUrl = useMemo(() => resolveAssetUrl(currentData?.watermarkUrl), [currentData?.watermarkUrl]);

    if (!isOpen || !currentData) return null;

    const displayRow = (label: string, value: string | null | undefined) => (
        <div className='col-md-6 mb-3'>
            <label className='modal_label fw-semibold'>{label}</label>
            <div className='form-control-plaintext border rounded p-2 bg-light'>{value || '-'}</div>
        </div>
    );

    const removeBranding = async (assetType: 'logo' | 'watermark') => {
        if (!currentData?.id || removing) return;
        try {
            setRemoving(assetType);
            const updated = await deleteCompanyGroupBrandingAsset(currentData.id, assetType);
            setCurrentData(updated);
            onBrandingUpdated?.(updated);
            toast.success(`${assetType === 'logo' ? 'Logo' : 'Watermark'} removed successfully.`, {
                position: 'top-center',
            });
        } catch (error: any) {
            console.error(`Failed to remove ${assetType}:`, error);
            showErrorToast(
                error,
                `Failed to remove ${assetType === 'logo' ? 'logo' : 'watermark'}.`,
                'delete'
            );
        } finally {
            setRemoving(null);
        }
    };

    const brandingCard = (label: string, assetType: 'logo' | 'watermark', imageUrl: string | null) => (
        <div className='col-md-6 mb-3'>
            <label className='modal_label fw-semibold'>{label}</label>
            <div className='border rounded p-3 bg-light position-relative' style={{minHeight: 170}}>
                {imageUrl ? (
                    <>
                        <button
                            type='button'
                            className='btn btn-sm btn-light-danger position-absolute'
                            style={{top: 8, right: 8, width: 26, height: 26, padding: 0, lineHeight: '24px'}}
                            title={`Remove ${label.toLowerCase()}`}
                            onClick={() => removeBranding(assetType)}
                            disabled={Boolean(removing)}
                        >
                            ×
                        </button>
                        <a href={imageUrl} target='_blank' rel='noreferrer' title='Open file'>
                            <img
                                src={imageUrl}
                                alt={label}
                                style={{maxWidth: '100%', maxHeight: 130, objectFit: 'contain', display: 'block', margin: '8px auto 0'}}
                            />
                        </a>
                    </>
                ) : (
                    <span className='text-muted'>Not uploaded</span>
                )}
            </div>
        </div>
    );

    return (
        <div className='modal-overlay'>
            <div
                className='modal-content'
                onClick={(e) => e.stopPropagation()}
                style={{maxWidth: '60rem', background: '#fff', borderRadius: '10px'}}
            >
                <div className='custom-modal-header d-flex justify-content-between align-items-center border-bottom p-3'>
                    <h5 className='m-0'>View Company Details</h5>
                    <button className='close-btn btn btn-sm btn-light' onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className='custom-modal-body p-4'>
                    <div className='row'>
                        {displayRow('Company Name', currentData.name)}
                        {displayRow('Contact No.', currentData.contactNo)}
                        {displayRow('Alternative Contact No.', currentData.altContactNo)}
                        {displayRow('Email', currentData.email)}
                        {displayRow('Address Line 1', currentData.addressLine1)}
                        {displayRow('Address Line 2', currentData.addressLine2)}
                        {displayRow('Landmark', currentData.landmark)}
                        {displayRow('Country', currentData.country)}
                        {displayRow('State', currentData.state)}
                        {displayRow('City', currentData.city)}
                        {displayRow('Primary Currency', currentData.primaryCurrencyCode)}
                        {displayRow(
                            'Company Details',
                            currentData.companyGroupAdminDetails || currentData.companyDetails
                        )}
                        {displayRow('GST Number', currentData.gstNumber)}
                        {displayRow('PAN Number', currentData.panNumber)}
                        {displayRow('VAT Number', currentData.vatNumber)}
                    </div>

                    <div className='mt-2'>
                        <h6 className='fw-semibold mb-3'>Branding Files</h6>
                        <div className='row'>
                            {brandingCard('Company Logo', 'logo', logoUrl)}
                            {brandingCard('Company Watermark', 'watermark', watermarkUrl)}
                        </div>
                    </div>
                </div>

                <div className='d-flex justify-content-end border-top p-3'>
                    <button className='btn btn-secondary' onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewCompanyAdminModal;
